// Surface: a smooth slab (a mid-surface and a thickness) fitted to the noisy points of one part, for
// seats, table tops, panels. The mid-surface is a cubic B-spline height field over the points' best
// plane, smoothed by a thin-plate penalty and fitted with Huber weights. A slab scanned from both
// sides has points on two faces, t apart: oriented normals say which face a point is on; without them
// a two-component mixture on the residuals does (it can't once the thickness is under the noise).
// The occupied part of the domain is kept as a mask, so an L-shaped top stays L-shaped.
import { Band, solveSPD } from './linalg.ts';
import { basis, surf, penalty2, lambda } from './bspline.ts';
import { sigmaMAD, huberW, quantile } from './robust.ts';
import { pca } from './pole.ts';
import { encodeSet } from '../edit/pidset.ts';
import type { SurfacePiece } from './doc.ts';

export interface SurfaceOpts {
  /** Control point spacing (m; 0: about 1/24 of the size, at least 4 cm). */
  cell?: number;
  /** Smoothness (dimensionless; 1 by default). */
  s?: number;
  /** Thickness (m; 0: fit it). */
  t?: number;
  iters?: number;
}

export interface SurfaceFit extends SurfacePiece {
  /** How the faces were told apart. */
  sides: 'normals' | 'mixture' | 'one';
  area: number;
  inliers: number;
  warn: string[];
}

type V = [number, number, number];
const cross = (a: V, b: V): V => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const neg = (a: V): V => [-a[0], -a[1], -a[2]];

export function fitSurface(xyz: Float32Array, nrm: Float32Array | null, o: SurfaceOpts = {}): SurfaceFit {
  const n = xyz.length / 3, warn: string[] = [], sm = o.s ?? 1, tGiven = o.t && o.t > 0 ? o.t : 0;
  if (n < 30) throw new Error('too few points for a surface');
  // ── the plane: normal up unless vertical (as fitPlane), u the long axis with a fixed sign
  const pc = pca(xyz);
  let N = pc.axes[2], U = pc.axes[0];
  if (N[2] < 0 || (Math.abs(N[2]) < 1e-9 && (N[1] < 0 || (N[1] === 0 && N[0] < 0)))) N = neg(N);
  if (U[0] < -1e-9 || (Math.abs(U[0]) <= 1e-9 && (U[1] < -1e-9 || (Math.abs(U[1]) <= 1e-9 && U[2] < 0)))) U = neg(U);
  const Vv = cross(N, U), c = pc.c;
  const A = new Float64Array(n), B = new Float64Array(n), W = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    const d = [xyz[3 * i] - c[0], xyz[3 * i + 1] - c[1], xyz[3 * i + 2] - c[2]];
    A[i] = d[0] * U[0] + d[1] * U[1] + d[2] * U[2];
    B[i] = d[0] * Vv[0] + d[1] * Vv[1] + d[2] * Vv[2];
    W[i] = d[0] * N[0] + d[1] * N[1] + d[2] * N[2];
  }
  // ── the domain and the control grid
  const qa = [quantile(A, 0.005), quantile(A, 0.995)], qb = [quantile(B, 0.005), quantile(B, 0.995)];
  const cell = o.cell && o.cell > 0 ? o.cell : Math.max(0.04, Math.max(qa[1] - qa[0], qb[1] - qb[0]) / 24);
  const a0 = qa[0] - cell / 2, a1 = qa[1] + cell / 2, b0 = qb[0] - cell / 2, b1 = qb[1] + cell / 2, la = a1 - a0, lb = b1 - b0;
  const nu = Math.max(4, Math.min(40, Math.round(la / cell) + 3)), nv = Math.max(4, Math.min(40, Math.round(lb / cell) + 3));
  // the design matrix doesn't change: each point's 16 basis weights and first control index
  const inside = new Uint8Array(n), k0 = new Int32Array(n), Bw = new Float64Array(16 * n), Nu = new Float64Array(4), Nv = new Float64Array(4);
  for (let i = 0; i < n; i++) {
    if (A[i] < a0 || A[i] > a1 || B[i] < b0 || B[i] > b1) continue;
    inside[i] = 1;
    const su = basis((A[i] - a0) / la, nu, Nu), sv = basis((B[i] - b0) / lb, nv, Nv);
    k0[i] = su * nv + sv;
    for (let p = 0; p < 4; p++) for (let q = 0; q < 4; q++) Bw[16 * i + 4 * p + q] = Nu[p] * Nv[q];
  }
  const Pen = penalty2(nu, nv, la, lb);
  let H: Float64Array = new Float64Array(nu * nv);
  const S = new Float64Array(n), Sa = new Float64Array(n), Sb = new Float64Array(n), out = [0, 0, 0];
  const evalS = () => {
    for (let i = 0; i < n; i++) {
      surf(H, nu, nv, Math.min(1, Math.max(0, (A[i] - a0) / la)), Math.min(1, Math.max(0, (B[i] - b0) / lb)), out);
      S[i] = out[0]; Sa[i] = out[1] / la; Sb[i] = out[2] / lb;
    }
  };
  const solveH = (y: Float64Array, w: Float64Array) => {
    const M = new Band(nu * nv, 3 * nv + 3), rhs = new Float64Array(nu * nv);
    for (let i = 0; i < n; i++) {
      const wi = w[i];
      if (!wi || !inside[i]) continue;
      const base = 16 * i;
      for (let p = 0; p < 16; p++) {
        const ip = k0[i] + (p >> 2) * nv + (p & 3), bp = Bw[base + p] * wi;
        if (!bp) continue;
        rhs[ip] += bp * y[i];
        for (let q = 0; q <= p; q++) {
          const iq = k0[i] + (q >> 2) * nv + (q & 3);
          M.add(ip, iq, bp * Bw[base + q]);
        }
      }
    }
    if (sm > 0) M.addScaled(Pen, lambda(sm, M, Pen));
    H = solveSPD(M, rhs);
    evalS();
  };

  // ── sides: which face each point is on (-1, +1, or soft in between), and the thickness
  const side = new Float64Array(n), y = new Float64Array(n), r = new Float64Array(n);
  let w = Float64Array.from(inside), t = tGiven, sides: SurfaceFit['sides'] = 'mixture', sigma = 0;
  const fitMid = (trim: boolean) => {
    for (let i = 0; i < n; i++) y[i] = W[i] - (side[i] * t) / 2;
    solveH(y, w);
    // a soft side (the mixture) would absorb the noise: there, the residual is to the nearer face
    for (let i = 0; i < n; i++) {
      const rho = W[i] - S[i];
      r[i] = sides === 'mixture' ? (Math.abs(rho - t / 2) < Math.abs(rho + t / 2) ? rho - t / 2 : rho + t / 2) : rho - (side[i] * t) / 2;
    }
    sigma = sigmaMAD(r, w) || 1e-4;
    huberW(r, sigma, trim ? 3.5 : 0, w);
    for (let i = 0; i < n; i++) if (!inside[i]) w[i] = 0;
  };
  fitMid(false); fitMid(true);

  /** Face of each point from its normal against the surface's (0: edge-on or no normal); null if too few have normals. */
  const normalSides = () => {
    if (!nrm) return null;
    const s = new Float64Array(n);
    let good = 0;
    for (let i = 0; i < n; i++) {
      const nx = nrm[3 * i], ny = nrm[3 * i + 1], nz = nrm[3 * i + 2];
      if (nx !== nx || !(nx || ny || nz)) continue;
      const pa = nx * U[0] + ny * U[1] + nz * U[2], pb = nx * Vv[0] + ny * Vv[1] + nz * Vv[2], pw = nx * N[0] + ny * N[1] + nz * N[2];
      const l = Math.hypot(pa, pb, pw) * Math.hypot(Sa[i], Sb[i], 1), d = l ? (-pa * Sa[i] - pb * Sb[i] + pw) / l : 0;
      if (Math.abs(d) > 0.5) { s[i] = Math.sign(d); good++; }
    }
    return good >= 0.7 * n ? s : null;
  };
  const ns = normalSides();
  if (ns) {
    let np = 0, nn = 0, sx = 0, sy = 0, sxx = 0, syy = 0, sxy = 0, m = 0;
    for (let i = 0; i < n; i++) {
      if (!w[i] || !ns[i]) continue;
      if (ns[i] > 0) np++; else nn++;
      const rho = W[i] - S[i];
      sx += ns[i]; sy += rho; sxx += 1; syy += rho * rho; sxy += ns[i] * rho; m++;
    }
    const corr = (sxy / m - (sx / m) * (sy / m)) / Math.sqrt(Math.max(1e-30, (sxx / m - (sx / m) ** 2) * (syy / m - (sy / m) ** 2)));
    if (Math.min(np, nn) < 0.05 * (np + nn)) {
      sides = 'one';
      const s0 = np >= nn ? 1 : -1;
      side.fill(s0);
      if (!tGiven) warn.push('one face seen: thickness not measurable (set it)');
    } else if (corr > 0.1) { sides = 'normals'; side.set(ns); }
  }
  // mixture: residuals to the mid-surface are ±t/2 plus noise
  let pi = tGiven ? 0.6 : 0.5, mh = tGiven / 2, sg = 0;
  const em = (steps: number) => {
    for (let k = 0; k < steps; k++) {
      let sw = 0, sg2 = 0, sm2 = 0, sp = 0;
      for (let i = 0; i < n; i++) {
        if (!w[i]) continue;
        const rho = W[i] - S[i], lp = Math.log(pi) - ((rho - mh) ** 2) / (2 * sg * sg), ln = Math.log(1 - pi) - ((rho + mh) ** 2) / (2 * sg * sg);
        const g = 1 / (1 + Math.exp(Math.max(-50, Math.min(50, ln - lp))));
        side[i] = 2 * g - 1;
        sw += w[i]; sp += w[i] * g; sm2 += w[i] * side[i] * rho;
      }
      pi = Math.min(0.995, Math.max(0.005, sp / sw));
      if (!tGiven) mh = Math.max(0, sm2 / sw);
      for (let i = 0; i < n; i++) if (w[i]) { const rho = W[i] - S[i], g = (side[i] + 1) / 2; sg2 += w[i] * (g * (rho - mh) ** 2 + (1 - g) * (rho + mh) ** 2); }
      sg = Math.max(1e-4, Math.sqrt(sg2 / sw));
    }
  };
  if (sides === 'mixture') {
    let s2 = 0, sw = 0;
    for (let i = 0; i < n; i++) if (w[i]) { s2 += w[i] * (W[i] - S[i]) ** 2; sw += w[i]; }
    const sd = Math.sqrt(s2 / sw);
    if (!tGiven) mh = 0.9 * sd;
    sg = Math.max(1e-4, tGiven ? sd : 0.5 * sd);
    em(30);
    t = 2 * mh;
  }
  const iters = o.iters ?? 8;
  for (let it = 0; it < iters; it++) {
    fitMid(true);
    if (sides === 'normals') {
      if (it % 2 === 1) { const s2 = normalSides(); if (s2) side.set(s2); }
      if (!tGiven) {
        let a = 0, b = 0;
        for (let i = 0; i < n; i++) if (w[i]) { a += w[i] * side[i] * (W[i] - S[i]); b += w[i] * side[i] * side[i]; }
        t = Math.max(0, b ? (2 * a) / b : 0);
      }
    } else if (sides === 'mixture') { em(10); t = 2 * mh; }
  }
  if (sides === 'mixture') {
    if (pi < 0.05 || pi > 0.95) { sides = 'one'; if (!tGiven) { t = 0; warn.push('one face seen: thickness not measurable (set it)'); } }
    else if (!tGiven && mh < sg) warn.push('thickness ≈ noise-limited');
  }
  fitMid(true);
  let ss = 0, inl = 0;
  for (let i = 0; i < n; i++) if (w[i] > 0) { ss += r[i] * r[i]; inl++; }
  const rms = Math.sqrt(ss / Math.max(1, inl));

  // ── the mask: occupied cells (2+ inliers), closed by one cell, largest 8-connected part
  let cm = Math.max(cell / 2, 0.02);
  cm = Math.max(cm, la / 256, lb / 256);
  const mw = Math.max(1, Math.min(256, Math.ceil(la / cm))), mh2 = Math.max(1, Math.min(256, Math.ceil(lb / cm)));
  const cnt = new Uint16Array(mw * mh2);
  for (let i = 0; i < n; i++) if (w[i] > 0) {
    const x = Math.min(mw - 1, Math.floor(((A[i] - a0) / la) * mw)), yy = Math.min(mh2 - 1, Math.floor(((B[i] - b0) / lb) * mh2));
    cnt[yy * mw + x]++;
  }
  let occ = Uint8Array.from(cnt, x => (x >= 2 ? 1 : 0));
  const morph = (m: Uint8Array, grow: boolean) => {
    const o2 = new Uint8Array(m.length);
    for (let yy = 0; yy < mh2; yy++) for (let x = 0; x < mw; x++) {
      let any = false, all = true;
      for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
        const X = x + dx, Y = yy + dy, v = X >= 0 && Y >= 0 && X < mw && Y < mh2 ? m[Y * mw + X] : 0;
        if (v) any = true; else all = false;
      }
      o2[yy * mw + x] = (grow ? any : all) ? 1 : 0;
    }
    return o2;
  };
  occ = morph(morph(occ, true), false);
  const lab = new Int32Array(mw * mh2).fill(-1);
  let best = -1, bestN = 0;
  for (let s0 = 0, comp = 0; s0 < occ.length; s0++) {
    if (!occ[s0] || lab[s0] >= 0) continue;
    const stack = [s0];
    lab[s0] = comp;
    let k = 0;
    while (stack.length) {
      const q = stack.pop()!, x = q % mw, yy = (q - x) / mw;
      k++;
      for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
        const X = x + dx, Y = yy + dy, j = Y * mw + X;
        if (X >= 0 && Y >= 0 && X < mw && Y < mh2 && occ[j] && lab[j] < 0) { lab[j] = comp; stack.push(j); }
      }
    }
    if (k > bestN) { bestN = k; best = comp; }
    comp++;
  }
  const cells: number[] = [];
  for (let j = 0; j < lab.length; j++) if (lab[j] === best && best >= 0) cells.push(j);
  const area = cells.length * (la / mw) * (lb / mh2);

  // ── warnings
  let steep = 0;
  for (let i = 0; i < n; i++) if (w[i] > 0 && Math.hypot(Sa[i], Sb[i]) > 1) steep++;
  if (steep > 0.05 * inl) warn.push('strongly folded: split the label');
  if (inl < 0.7 * n) warn.push('noisy: under 70% of the points fit');
  return {
    o: [...c], u: [...U], v: [...Vv], ab: [a0, a1, b0, b1], nu, nv, h: [...H], t,
    mask: { w: mw, h: mh2, set: encodeSet(cells) }, rms, n: inl,
    sides, area, inliers: inl / n, warn,
  };
}
